import { useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { INDUSTRY_SOLUTIONS } from '../config/constants';

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ── Industry SVG illustrations (no stock photos) ─────── */
const IndustryVisual = ({ id, color = '#00D4FF' }) => {
  const visuals = {
    'auto-components': (
      <svg viewBox="0 0 240 160" className="w-full h-full">
        <defs>
          <linearGradient id="autoG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* Gear shape */}
        <motion.circle cx="80" cy="75" r="30" fill="none" stroke="url(#autoG)" strokeWidth="1.5"
          initial={{ rotate: 0 }} animate={{ rotate: 360 }} transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: '80px 75px' }} />
        <motion.circle cx="80" cy="75" r="18" fill="none" stroke="url(#autoG)" strokeWidth="1"
          initial={{ rotate: 0 }} animate={{ rotate: -360 }} transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: '80px 75px' }} />
        {/* Gear teeth */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
          const rad = (angle * Math.PI) / 180;
          const x1 = 80 + 28 * Math.cos(rad);
          const y1 = 75 + 28 * Math.sin(rad);
          const x2 = 80 + 36 * Math.cos(rad);
          const y2 = 75 + 36 * Math.sin(rad);
          return (
            <motion.line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#00D4FF" strokeWidth="3" strokeLinecap="round"
              initial={{ opacity: 0 }} whileInView={{ opacity: 0.5 }} transition={{ delay: i * 0.05 }} />
          );
        })}
        {/* Assembly line */}
        <motion.line x1="130" y1="75" x2="220" y2="75" stroke="url(#autoG)" strokeWidth="1" strokeDasharray="4 3"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.5, duration: 0.8 }} />
        {/* Small component boxes on line */}
        {[145, 170, 195].map((x, i) => (
          <motion.rect key={i} x={x - 6} y={68} width="12" height="14" rx="2"
            fill="none" stroke="#00D4FF" strokeWidth="1"
            initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 0.6, y: 0 }}
            transition={{ delay: 0.8 + i * 0.15 }} />
        ))}
        <motion.text x="120" y="145" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="Space Grotesk" textAnchor="middle">
          OEM Production Pipeline
        </motion.text>
      </svg>
    ),
    fabrication: (
      <svg viewBox="0 0 240 160" className="w-full h-full">
        <defs>
          <linearGradient id="fabG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Sheet metal bend */}
        <motion.path d="M40 90 L90 90 L110 50 L200 50" fill="none" stroke="url(#fabG)" strokeWidth="2"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 1 }} />
        {/* Bend angle indicator */}
        <motion.path d="M95 80 Q100 70 105 60" fill="none" stroke="#F59E0B" strokeWidth="1" strokeDasharray="2 2"
          initial={{ opacity: 0 }} whileInView={{ opacity: 0.6 }} transition={{ delay: 0.8 }} />
        {/* Dimension lines */}
        <motion.line x1="40" y1="100" x2="90" y2="100" stroke="#00D4FF" strokeWidth="0.8"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 1 }} style={{ transformOrigin: 'left' }} />
        <motion.text x="65" y="112" fill="#00D4FF" fontSize="7" textAnchor="middle" fillOpacity="0.6">50mm</motion.text>
        <motion.line x1="110" y1="40" x2="200" y2="40" stroke="#00D4FF" strokeWidth="0.8"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 1.1 }} style={{ transformOrigin: 'left' }} />
        <motion.text x="155" y="35" fill="#00D4FF" fontSize="7" textAnchor="middle" fillOpacity="0.6">90mm</motion.text>
        {/* Material yield indicator */}
        <motion.rect x="150" y="70" width="60" height="30" rx="4" fill="none" stroke="#F59E0B" strokeWidth="1" strokeDasharray="3 2"
          initial={{ opacity: 0 }} whileInView={{ opacity: 0.5 }} transition={{ delay: 1.2 }} />
        <motion.text x="180" y="88" fill="rgba(255,255,255,0.3)" fontSize="7" textAnchor="middle">Yield: 92%</motion.text>
        <motion.text x="120" y="145" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="Space Grotesk" textAnchor="middle">
          Process-specific Costing
        </motion.text>
      </svg>
    ),
    'tool-die': (
      <svg viewBox="0 0 240 160" className="w-full h-full">
        <defs>
          <linearGradient id="tdG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Die block */}
        <motion.rect x="60" y="40" width="120" height="70" rx="4" fill="none" stroke="url(#tdG)" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 0.8 }} />
        {/* Die cavity */}
        <motion.path d="M90 110 Q120 80 150 110" fill="none" stroke="#8B5CF6" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.5, duration: 0.6 }} />
        {/* Precision marks */}
        {[80, 100, 120, 140, 160].map((x, i) => (
          <motion.line key={i} x1={x} y1="38" x2={x} y2="42" stroke="#8B5CF6" strokeWidth="0.8"
            initial={{ opacity: 0 }} whileInView={{ opacity: 0.5 }} transition={{ delay: 0.8 + i * 0.05 }} />
        ))}
        {/* Phase indicators */}
        {['Design', 'Trial', 'Production'].map((phase, i) => (
          <motion.g key={phase}>
            <motion.circle cx={80 + i * 40} cy="135" r="4" fill={i === 2 ? '#8B5CF6' : 'none'}
              stroke="#8B5CF6" strokeWidth="1" fillOpacity="0.3"
              initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 1 + i * 0.15 }} />
            {i < 2 && <motion.line x1={84 + i * 40} y1="135" x2={116 + i * 40} y2="135"
              stroke="#8B5CF6" strokeWidth="0.8" strokeDasharray="2 2"
              initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 1.1 + i * 0.15 }} />}
          </motion.g>
        ))}
        <motion.text x="120" y="152" fill="rgba(255,255,255,0.25)" fontSize="7" fontFamily="Space Grotesk" textAnchor="middle">
          Precision Tooling Lifecycle
        </motion.text>
      </svg>
    ),
    engineering: (
      <svg viewBox="0 0 240 160" className="w-full h-full">
        <defs>
          <linearGradient id="engG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Building structure */}
        <motion.rect x="70" y="40" width="100" height="80" rx="2" fill="none" stroke="url(#engG)" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 0.8 }} />
        {/* Floor lines */}
        {[60, 80, 100].map((y, i) => (
          <motion.line key={i} x1="70" y1={y} x2="170" y2={y} stroke="url(#engG)" strokeWidth="0.6"
            initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: 0.5 + i * 0.1 }} style={{ transformOrigin: 'left' }} />
        ))}
        {/* Connection lines to external nodes */}
        {[[40, 50], [200, 60], [40, 100], [200, 110]].map(([x, y], i) => (
          <motion.g key={i}>
            <motion.line x1={x < 120 ? 70 : 170} y1={y} x2={x} y2={y}
              stroke="#14B8A6" strokeWidth="0.8" strokeDasharray="3 2"
              initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.8 + i * 0.1 }} />
            <motion.circle cx={x} cy={y} r="4" fill="#14B8A6" fillOpacity="0.2" stroke="#14B8A6" strokeWidth="1"
              initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 1 + i * 0.1 }} />
          </motion.g>
        ))}
        <motion.text x="120" y="145" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="Space Grotesk" textAnchor="middle">
          Multi-discipline Coordination
        </motion.text>
      </svg>
    ),
  };
  return visuals[id] || null;
};

export const SolutionsPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location]);

  return (
    <div className="pt-24" data-testid="solutions-page">
      {/* Hero */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#00D4FF]/5 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/20 text-[#00D4FF] text-sm font-medium"
              data-testid="solutions-hero-badge">
              Industry Solutions
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Built for <span className="text-gradient-cyan">Manufacturing</span>
            </h1>
            <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto">
              Cost Genie understands the unique challenges of different manufacturing segments.
              Our platform adapts to your specific workflows and requirements.
            </p>
          </motion.div>
        </div>

        {/* Quick Nav Pills */}
        <div className="max-w-7xl mx-auto px-6 mt-12">
          <div className="flex flex-wrap justify-center gap-3">
            {INDUSTRY_SOLUTIONS.map((sol) => (
              <a key={sol.id} href={`#${sol.id}`}
                className="px-5 py-2.5 rounded-full bg-white/[0.03] border border-white/10 text-white/60 hover:bg-white/[0.06] hover:text-white transition-all flex items-center gap-2 text-sm"
                data-testid={`solution-nav-${sol.id}`}>
                <sol.icon className="w-4 h-4" />
                {sol.title.split(' ')[0]}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Stories */}
      {INDUSTRY_SOLUTIONS.map((solution, index) => {
        const isReversed = index % 2 === 1;
        return (
          <section key={solution.id} id={solution.id}
            className={`py-20 md:py-28 ${index % 2 === 1 ? 'bg-white/[0.01]' : ''}`}
            data-testid={`solution-${solution.id}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
                {/* Visual */}
                <AnimatedSection className={isReversed ? 'lg:order-2' : ''}>
                  <div className="relative aspect-[3/2] max-w-lg mx-auto">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 overflow-hidden">
                      <div className="absolute inset-0 p-4">
                        <IndustryVisual id={solution.id} />
                      </div>
                    </div>
                    {/* Floating metric card */}
                    <motion.div
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -bottom-6 -right-4 glass-card p-4"
                    >
                      <p className="text-2xl font-bold text-[#00D4FF]">{solution.results[0].metric}</p>
                      <p className="text-white/40 text-xs">{solution.results[0].label}</p>
                    </motion.div>
                  </div>
                </AnimatedSection>

                {/* Content */}
                <AnimatedSection className={isReversed ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center">
                      <solution.icon className="w-6 h-6 text-[#00D4FF]" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold">{solution.title}</h2>
                      <p className="text-white/40 text-xs mt-0.5">{solution.subtitle}</p>
                    </div>
                  </div>

                  <p className="text-white/50 text-sm mb-8 leading-relaxed">{solution.description}</p>

                  {/* Challenges */}
                  <div className="mb-8">
                    <h3 className="text-xs font-semibold text-white/40 uppercase tracking-[0.15em] mb-4">
                      Challenges We Solve
                    </h3>
                    <div className="space-y-3">
                      {solution.challenges.map((challenge, i) => (
                        <motion.div key={i} className="flex items-start gap-3"
                          initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                          <CheckCircle2 className="w-4 h-4 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                          <span className="text-white/70 text-sm">{challenge}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Results */}
                  <div className="grid grid-cols-3 gap-3">
                    {solution.results.map((result, i) => (
                      <motion.div key={i} className="text-center p-4 rounded-xl bg-white/[0.03] border border-white/5"
                        initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1 }}>
                        <div className="text-xl font-bold text-[#00D4FF]">{result.metric}</div>
                        <div className="text-white/40 text-xs mt-1">{result.label}</div>
                      </motion.div>
                    ))}
                  </div>
{/* <Link to="/demo" className="inline-flex items-center gap-2 text-[#00D4FF] font-medium hover:gap-3 transition-all mt-6 text-sm"
  data-testid={`solutions-cta-${i}`}>
  Request Demo
  <ArrowUpRight className="w-4 h-4" />
</Link> */}
                </AnimatedSection>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="glass-card p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#00D4FF]/10 rounded-full blur-[100px]" />
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Don't See Your Industry?</h2>
                <p className="text-white/50 mb-8 max-w-xl mx-auto text-sm">
                  Cost Genie adapts to any manufacturing RFQ workflow. Let's discuss your specific requirements.
                </p>
                {/* <Link to="/demo"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all"
                  data-testid="solutions-demo-btn">
                  Talk to Us <ArrowRight className="w-5 h-5" />
                </Link> */}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;
